/**
 * Extraction d'une archive ZIP côté navigateur et détection des fichiers
 * logiques de l'import (eleves.csv, enseignants.csv, …).
 *
 * Le bundle est rapproché des fichiers *attendus* par les steps sélectionnés :
 * on accepte les entrées CSV dont le nom de base (sans dossier ni extension)
 * correspond à un nom logique — alias tolérés : anciens noms anglais
 * (students, teachers, directors) et variantes accentuées (élèves) — on
 * signale celles qu'on ignore et celles qui manquent encore.
 */

import { unzip } from 'fflate';
import type { LogicalFileSpec } from './stepCatalog';

export interface ZipDetectionEntry {
  /** Nom logique reconnu (ex: "eleves"). */
  logicalName: string;
  /** Nom du fichier tel que présent dans l'archive. */
  fileName: string;
}

/**
 * Alias de noms de fichiers acceptés → nom logique canonique (anciens noms
 * anglais des modèles). Les variantes accentuées (« Élèves », « Matières »)
 * sont couvertes par la normalisation de `canonicalLogicalKey`.
 */
const FILE_ALIASES: Record<string, string> = {
  students: 'eleves',
  teachers: 'enseignants',
  directors: 'direction',
  directeurs: 'direction',
};

/**
 * Normalise un nom de fichier (sans extension) ou d'onglet Excel vers un nom
 * logique : minuscules, accents retirés, alias historiques résolus.
 */
export function canonicalLogicalKey(name: string): string {
  const key = name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return FILE_ALIASES[key] ?? key;
}

export interface ZipDetectionResult {
  /** Fichiers reconnus, prêts à être ajoutés au bundle. */
  matched: Array<ZipDetectionEntry & { file: File }>;
  /** Entrées CSV présentes mais non attendues par la sélection courante. */
  ignored: string[];
  /** Noms logiques attendus mais absents de l'archive. */
  missing: string[];
}

/** Nom de base d'un chemin d'archive, sans dossier ni extension `.csv`. */
function logicalKeyFromPath(path: string): string | null {
  const base = path.split('/').pop() ?? path;
  // Ignore les entrées système / cachées (__MACOSX/, .DS_Store, dossiers).
  if (base === '' || base.startsWith('.') || path.startsWith('__MACOSX')) {
    return null;
  }
  const match = /^(.+)\.csv$/i.exec(base);
  if (!match) return null;
  return canonicalLogicalKey(match[1]);
}

/** Décompresse l'archive en mémoire → map chemin → octets. */
function inflate(bytes: Uint8Array): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => {
    unzip(bytes, (err, files) => {
      if (err) reject(err);
      else resolve(files);
    });
  });
}

/**
 * Lit un ZIP et rapproche son contenu des fichiers attendus.
 *
 * @param zip       Archive sélectionnée par l'utilisateur.
 * @param expected  Fichiers logiques requis par les steps sélectionnés.
 */
export async function detectZipBundle(
  zip: File,
  expected: LogicalFileSpec[],
): Promise<ZipDetectionResult> {
  const bytes = new Uint8Array(await zip.arrayBuffer());
  const entries = await inflate(bytes);

  const expectedByKey = new Map(expected.map((s) => [s.logicalName.toLowerCase(), s.logicalName]));

  const matched: ZipDetectionResult['matched'] = [];
  const ignored: string[] = [];
  const seen = new Set<string>();

  for (const [path, content] of Object.entries(entries)) {
    // fflate liste aussi les dossiers (contenu vide, chemin terminé par /).
    if (path.endsWith('/')) continue;
    const key = logicalKeyFromPath(path);
    if (key === null) continue;

    const logicalName = expectedByKey.get(key);
    const fileName = path.split('/').pop() ?? path;
    if (!logicalName || seen.has(logicalName)) {
      ignored.push(fileName);
      continue;
    }

    seen.add(logicalName);
    // `content` est typé Uint8Array<ArrayBufferLike> par fflate ; on le recopie
    // dans un buffer dédié pour satisfaire la signature BlobPart de File().
    const bytes = new Uint8Array(content.length);
    bytes.set(content);
    matched.push({
      logicalName,
      fileName,
      file: new File([bytes.buffer], fileName, { type: 'text/csv' }),
    });
  }

  const missing = expected.map((s) => s.logicalName).filter((name) => !seen.has(name));

  return { matched, ignored, missing };
}
